import { useApp } from 'contexts/app';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Text } from 'shell/components';
import { SYSTEM_ASSIGNMENT_DEFAULTS } from 'utils/assignmentDefaults';
import { getFeedbackSettingsErrors, hasAssignmentSettingsErrors } from 'utils/assignmentSettingsValidation';
import { SettingsToggleCard, useClearAssignmentSettingError } from './AssignmentSettingsPanelControls';
import FeedbackSettingsForm from './FeedbackSettingsForm';
import FeedbackTypeCard from './FeedbackTypeCard';
import StudentFeedbackSettingsForm from './StudentFeedbackSettingsForm';

const AssignmentFeedbackPanel = ({ form, setForm, errors, setErrors, isEditable, student = false }) => {
    const { isFeedbackFeatureDisabled, isGradingFeatureDisabled } = useApp();
    const { t } = useTranslation('assignment');
    const { t: settingsT } = useTranslation('assignmentSettings');
    const id = useId();
    const field = student ? 'studentFeedbackSettings' : 'feedbackSettings';
    const contextField = student ? 'studentFeedbackCustomContext' : 'feedbackCustomContext';
    const enabledField = student ? 'aiStudentFeedbackEnabled' : 'aiFeedbackEnabled';
    // Institutional assignment-feedback restrictions apply to final submissions, as in the existing student dialog.
    const restricted = !student && isFeedbackFeatureDisabled;
    const enabled = form[enabledField] && !restricted;
    const disabled = !isEditable || !enabled;
    const settings = { ...SYSTEM_ASSIGNMENT_DEFAULTS[field], ...form[field], [contextField]: form[contextField] || '' };
    const detailErrors = getFeedbackSettingsErrors(form, student, { isGradingFeatureDisabled });
    const invalid = enabled && hasAssignmentSettingsErrors(detailErrors);
    useClearAssignmentSettingError(field, invalid, errors, setErrors);
    const Form = student ? StudentFeedbackSettingsForm : FeedbackSettingsForm;
    const handleChange = (changes) => {
        if (disabled) return;
        setForm((current) => {
            const value = {
                ...SYSTEM_ASSIGNMENT_DEFAULTS[field],
                ...current[field],
                [contextField]: current[contextField] || '',
            };
            const next = typeof changes === 'function' ? changes(value) : { ...value, ...changes };
            const { [contextField]: context, ...nextSettings } = next;
            return { ...current, [field]: nextSettings, [contextField]: context };
        });
    };
    return (
        <Stack spacing={5}>
            <SettingsToggleCard
                title={
                    student
                        ? t('formative-feedback.student-title', 'Formative Feedback for Students')
                        : t(
                              'feedback-section.final-submission-feedback-suggestions',
                              'Final Submission Feedback Suggestions'
                          )
                }
                description={
                    student
                        ? t(
                              'formative-feedback.student-description',
                              'Students can generate up to 3 rounds of AI feedback for assignments drafts before they submit. When you enable instant feedback, students will be able to see the assignment description and the assignment rubric.'
                          )
                        : t(
                              'feedback-section.feedback-description',
                              "TimelyGrader leverages assignment details to provide instructors with personalized feedback suggestions for student's final submissions."
                          )
                }
                checked={enabled}
                disabled={!isEditable}
                restricted={restricted}
                onChange={(value) => setForm((current) => ({ ...current, [enabledField]: value }))}
            />
            {(!student || enabled) && (
                <>
                    <Stack spacing={2} sx={{ minWidth: 0 }}>
                        <Text component={'h2'} variant={'p1'} weight={'medium'}>
                            {t('feedback-section.feedback-structure-label', 'Feedback Structure')}
                        </Text>
                        <Stack direction={'row'} spacing={5} sx={{ overflowX: 'auto', p: 1, pb: 3 }}>
                            {['rubric', 'system', 'templated', 'custom'].map((type) => (
                                <FeedbackTypeCard
                                    key={type}
                                    type={type}
                                    isSelected={settings.structure === type}
                                    disabled={
                                        disabled ||
                                        (type === 'templated' && (!form.aiGradingEnabled || isGradingFeatureDisabled))
                                    }
                                    onClick={() => handleChange({ structure: type })}
                                />
                            ))}
                        </Stack>
                        {(!form.aiGradingEnabled || isGradingFeatureDisabled) && (
                            <Text color={'light'}>
                                {settingsT(
                                    'panels.feedback.templatesRequireGrading',
                                    'Enable grading suggestions to use Smart Feedback Templates.'
                                )}
                            </Text>
                        )}
                    </Stack>
                    {invalid && (
                        <Text role={'alert'} color={'error'}>
                            {detailErrors.missingRubric
                                ? t('error.create-rubric', 'Please create a rubric to use Smart Feedback Templates.')
                                : settingsT(
                                      'panels.feedback.validation',
                                      'Complete the required feedback instructions and keep each field within its word limit.'
                                  )}
                        </Text>
                    )}
                    <Form
                        data={form}
                        form={settings}
                        errors={detailErrors}
                        handleChange={handleChange}
                        disabled={disabled}
                        idPrefix={`${id}-`}
                    />
                </>
            )}
        </Stack>
    );
};
export default AssignmentFeedbackPanel;
